import { useEffect, useMemo, useState } from "react";
import { getParsedStorageItem } from "common/src/utils/localStorageUtils";
import { isEmptyOrNull } from "common/src/utils/stringUtils";
import type { CustomView } from "../../../../datagrid/ColumnVisibilityModal";

export interface UseDiscoverGamesViewsArgs {
	storageKey: string;
}

export function useDiscoverGamesViews(args: UseDiscoverGamesViewsArgs) {
	const { storageKey } = args;
	const customViewsKey = `${storageKey}_customViews`;
	const selectedViewKey = `${storageKey}_selectedView`;

	const [customViews, setCustomViews] = useState<CustomView[]>(() =>
		getParsedStorageItem<CustomView[]>(customViewsKey, []),
	);

	const [selectedCustomViewId, setSelectedCustomViewId] = useState<string | null>(() => {
		const stored = localStorage.getItem(selectedViewKey);
		if (stored) {
			return stored;
		}
		const views = getParsedStorageItem<CustomView[]>(customViewsKey, []);
		const defaultView = views.find((v) => v.isDefault);
		return defaultView?.id ?? null;
	});

	useEffect(() => {
		localStorage.setItem(customViewsKey, JSON.stringify(customViews));
	}, [customViews, customViewsKey]);

	useEffect(() => {
		if (selectedCustomViewId) {
			localStorage.setItem(selectedViewKey, selectedCustomViewId);
		} else {
			localStorage.removeItem(selectedViewKey);
		}
	}, [selectedCustomViewId, selectedViewKey]);

	const selectedViewName = useMemo(() => {
		if (isEmptyOrNull(selectedCustomViewId)) {
			return null;
		}
		const view = customViews.find((v) => v.id === selectedCustomViewId);
		return view?.name ?? null;
	}, [selectedCustomViewId, customViews]);

	return {
		customViewsKey,
		selectedViewKey,
		customViews,
		setCustomViews,
		selectedCustomViewId,
		setSelectedCustomViewId,
		selectedViewName,
	};
}
